/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import InvoiceService from '../services/InvoiceService'
import clsx from 'clsx'

const InvoiceCreate = () => {
    const { currentUser } = useContext(AppContext)
    const { orderId } = useParams()
    const history = useHistory()
    
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [order, setOrder] = useState(null)
    const [invoiceItems, setInvoiceItems] = useState([])
    const [customerNote, setCustomerNote] = useState('')
    const [promoAmount, setPromoAmount] = useState(0)
    const [maxPromoAmount, setMaxPromoAmount] = useState(0)

    useEffect(() => {
        if (orderId) {
            fetchOrderForInvoice()
        }
    }, [orderId])

    const fetchOrderForInvoice = async () => {
        setLoading(true)
        try {
            const response = await InvoiceService.getOrderForInvoice(orderId)
            
            if (response.success) {
                const orderData = response.data
                setOrder(orderData)
                
                // Initialize invoice items with available quantities
                const availableItems = (orderData.order_item || orderData.orderItem)?.filter(item => 
                    (item.qtyOrdered || 0) > (item.qtyInvoiced || 0)
                ).map(item => {
                    const qtyInvoice = Math.max(0, (item.qtyOrdered || 0) - (item.qtyInvoiced || 0))
                    const price = parseFloat(item.price) || 0
                    const rowTotal = price * qtyInvoice
                    
                    return {
                        ...item,
                        qtyInvoice,
                        maxQtyInvoice: Math.max(0, (item.qtyOrdered || 0) - (item.qtyInvoiced || 0)),
                        taxAmount: item.taxAmount || 0,
                        discountAmount: item.discountAmount || 0,
                        rowTotal
                    }
                }) || []

                setInvoiceItems(availableItems)
                setMaxPromoAmount(orderData.promoAmount || 0)
                
                // Calculate initial totals
                calculateTotals(availableItems)
            } else {
                console.error('Failed to fetch order for invoice:', response.errorMessage || response.message)
                alert('Failed to load order details')
                history.push('/sales-orders')
            }
        } catch (error) {
            console.error('Error fetching order for invoice:', error)
            alert('Error loading order details')
            history.push('/sales-orders')
        } finally {
            setLoading(false)
        }
    }

    const updateItemQuantity = (orderItemId, quantity) => {
        const updatedItems = invoiceItems.map(item => {
            if (item.orderItemId === orderItemId) {
                const qtyInvoice = Math.min(Math.max(0, parseInt(quantity) || 0), item.maxQtyInvoice)
                const price = parseFloat(item.price) || 0
                const rowTotal = price * qtyInvoice
                
                return {
                    ...item,
                    qtyInvoice,
                    rowTotal
                }
            }
            return item
        })
        
        setInvoiceItems(updatedItems)
        calculateTotals(updatedItems)
    }

    const calculateTotals = (items) => {
        const totals = InvoiceService.calculateInvoiceTotals(items)
        return totals
    }

    const validateInvoice = () => {
        const selectedItems = invoiceItems.filter(item => item.qtyInvoice > 0)
        
        if (selectedItems.length === 0) {
            return ['At least one item must be selected for invoicing']
        }

        return InvoiceService.validateInvoiceItems(selectedItems)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        const errors = validateInvoice()
        if (errors.length > 0) {
            alert('Please fix the following errors:\n' + errors.join('\n'))
            return
        }

        setSubmitting(true)

        try {
            const selectedItems = invoiceItems.filter(item => item.qtyInvoice > 0)
            const totals = calculateTotals(selectedItems)
            
            const invoiceData = {
                orderId: order.orderId,
                billingAddressId: order.billing_address_id || order.billingAddressId,
                shippingAddressId: order.shipping_address_id || order.shippingAddressId,
                items: selectedItems.map(item => ({
                    orderItemId: item.orderItemId,
                    productId: item.productId,
                    name: item.name,
                    sku: item.sku,
                    price: item.price,
                    qtyInvoice: item.qtyInvoice,
                    taxAmount: (item.taxAmount || 0) * item.qtyInvoice,
                    discountAmount: (item.discountAmount || 0) * item.qtyInvoice,
                    rowTotal: item.rowTotal,
                    maxQtyInvoice: Number(item.qtyOrdered) - Number(item.qtyInvoiced)
                })),
                subTotalAmount: totals.subtotal,
                totalTaxAmount: totals.totalTax,
                totalDiscountAmount: totals.totalDiscount,
                totalQty: totals.totalQty,
                shippingAmount: order.shippingAmount || 0,
                shippingTaxAmount: order.shippingTaxAmount || 0,
                promoAmount: Math.min(promoAmount, maxPromoAmount),
                maxPromoAmount: maxPromoAmount,
                customerNote
            }

            const response = await InvoiceService.createInvoice(invoiceData)

            if (response.success) {
                alert('Invoice created successfully!')
                history.push('/sales-invoices')
            } else {
                alert('Failed to create invoice: ' + (response.errorMessage || response.message))
            }
        } catch (error) {
            console.error('Error creating invoice:', error)
            alert('Error creating invoice. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading order details...</p>
                </div>
            </div>
        )
    }

    if (!order) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <h4>Order not found or no items available for invoicing</h4>
                    <Link to="/sales-orders" className="btn btn-primary">
                        Back to Orders
                    </Link>
                </div>
            </div>
        )
    }

    const totals = calculateTotals(invoiceItems.filter(item => item.qtyInvoice > 0))
    const grandTotal = totals.grandTotal + (parseFloat(order.shippingAmount) || 0) + (parseFloat(order.shippingTaxAmount) || 0) - Math.min(promoAmount, maxPromoAmount)

    return (
        <div className="container-fluid">
            {/* Page Header */}
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4">
                <div className="mb-3 mb-lg-0">
                    <div className="d-flex align-items-center">
                        <Link to="/sales-invoices" className="btn btn-link p-0 me-3">
                            <i className="fas fa-arrow-left"></i>
                        </Link>
                        <div>
                            <h1 className="h3 mb-0">Create Invoice</h1>
                            <p className="text-muted mb-0">Order #{order.orderNo}</p>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* Left Column */}
                    <div className="col-lg-8">
                        {/* Order Information */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Order Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-lg-6 col-md-12 mb-3">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Order No:</strong></td>
                                                    <td>
                                                        <Link to={`/sales-orders/${order.orderId}`}>
                                                            {order.orderNo}
                                                        </Link>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Order Date:</strong></td>
                                                    <td>{InvoiceService.formatDate(order.createdAt)}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Customer:</strong></td>
                                                    <td>{order.customer?.fullName || 'N/A'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="col-lg-6 col-md-12 mb-3">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td><strong>Status:</strong></td>
                                                    <td>
                                                        <span className="badge badge-info">{order.status}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Payment Method:</strong></td>
                                                    <td>{(order.payment_method || order.paymentMethod)?.name || 'N/A'}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Shipping Method:</strong></td>
                                                    <td>{(order.shipping_method || order.shippingMethod)?.methodName || 'N/A'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Items */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Items to Invoice</h5>
                                <small className="text-muted">Select quantities to include in this invoice</small>
                            </div>
                            <div className="card-body">
                                {invoiceItems.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>Product</th>
                                                    <th className="d-none d-md-table-cell">SKU</th>
                                                    <th className="d-none d-lg-table-cell">Price</th>
                                                    <th className="d-none d-lg-table-cell">Ordered</th>
                                                    <th className="d-none d-lg-table-cell">Invoiced</th>
                                                    <th className="d-none d-lg-table-cell">Available</th>
                                                    <th>Qty to Invoice</th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {invoiceItems.map((item) => (
                                                    <tr key={item.orderItemId}>
                                                        <td>
                                                            <div>
                                                                <strong>{item.name}</strong>
                                                                <div className="text-muted small d-md-none">
                                                                    SKU: {item.sku} | Price: {InvoiceService.formatCurrency(item.price)}
                                                                </div>
                                                                <div className="text-muted small d-md-none">
                                                                    Ordered: {parseInt(item.qtyOrdered) || 0} | Available: {parseInt(item.maxQtyInvoice) || 0}
                                                                </div>
                                                                {item.description && (
                                                                    <div className="text-muted small">
                                                                        {item.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="d-none d-md-table-cell">{item.sku}</td>
                                                        <td className="d-none d-lg-table-cell">{InvoiceService.formatCurrency(item.price)}</td>
                                                        <td className="d-none d-lg-table-cell">{parseInt(item.qtyOrdered) || 0}</td>
                                                        <td className="d-none d-lg-table-cell">{parseInt(item.qtyInvoiced) || 0}</td>
                                                        <td className="d-none d-lg-table-cell">{parseInt(item.maxQtyInvoice) || 0}</td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control form-control-sm"
                                                                style={{ width: '80px', minWidth: '80px' }}
                                                                min="0"
                                                                max={item.maxQtyInvoice}
                                                                value={item.qtyInvoice}
                                                                onChange={(e) => updateItemQuantity(item.orderItemId, e.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <strong>{InvoiceService.formatCurrency(item.rowTotal)}</strong>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="fas fa-file-invoice fa-3x mb-3"></i>
                                        <p>No items available for invoicing</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Customer Notes */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Customer Notes</h5>
                            </div>
                            <div className="card-body">
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={customerNote}
                                    onChange={(e) => setCustomerNote(e.target.value)}
                                    placeholder="Enter any notes for this invoice..."
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="col-lg-4">
                        {/* Invoice Summary */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Invoice Summary</h5>
                            </div>
                            <div className="card-body">
                                <table className="table table-borderless">
                                    <tbody>
                                        <tr>
                                            <td>Subtotal:</td>
                                            <td className="text-end">{InvoiceService.formatCurrency(totals.subtotal)}</td>
                                        </tr>
                                        <tr>
                                            <td>Tax:</td>
                                            <td className="text-end">{InvoiceService.formatCurrency(totals.totalTax)}</td>
                                        </tr>
                                        <tr>
                                            <td>Discount:</td>
                                            <td className="text-end text-success">-{InvoiceService.formatCurrency(totals.totalDiscount)}</td>
                                        </tr>
                                        <tr>
                                            <td>Shipping:</td>
                                            <td className="text-end">{InvoiceService.formatCurrency(order.shippingAmount || 0)}</td>
                                        </tr>
                                        <tr>
                                            <td>Shipping Tax:</td>
                                            <td className="text-end">{InvoiceService.formatCurrency(order.shippingTaxAmount || 0)}</td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                                                    <span className="mb-1 mb-md-0">Promo Discount:</span>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        style={{ width: '100px', minWidth: '100px' }}
                                                        min="0"
                                                        max={maxPromoAmount}
                                                        step="0.01"
                                                        value={promoAmount}
                                                        onChange={(e) => setPromoAmount(Math.min(parseFloat(e.target.value) || 0, maxPromoAmount))}
                                                    />
                                                </div>
                                                <small className="text-muted d-block mt-1">Max: {InvoiceService.formatCurrency(maxPromoAmount)}</small>
                                            </td>
                                            <td className="text-end text-success">-{InvoiceService.formatCurrency(Math.min(promoAmount, maxPromoAmount))}</td>
                                        </tr>
                                        <tr className="border-top">
                                            <th>Grand Total:</th>
                                            <th className="text-end">{InvoiceService.formatCurrency(grandTotal)}</th>
                                        </tr>
                                        <tr>
                                            <td>Total Qty:</td>
                                            <td className="text-end">{parseInt(totals.totalQty) || 0}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Billing & Shipping Info */}
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Shipping Address</h5>
                            </div>
                            <div className="card-body">
                                {(order.sales_order_shipping_address || order.salesOrderShippingAddress) ? (
                                    <div className="address-details">
                                        <div className="address-line">
                                            <i className="fas fa-user text-muted me-2"></i>
                                            <strong>{(order.sales_order_shipping_address || order.salesOrderShippingAddress).fullName || 'N/A'}</strong>
                                        </div>
                                        <div className="address-line">
                                            <i className="fas fa-home text-muted me-2"></i>
                                            {((order.sales_order_shipping_address || order.salesOrderShippingAddress).house && `House: ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).house}`) || ''}
                                            {((order.sales_order_shipping_address || order.salesOrderShippingAddress).floor_no && `, Floor: ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).floor_no}`) || ''}
                                            {((order.sales_order_shipping_address || order.salesOrderShippingAddress).apartment_no && `, Flat: ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).apartment_no}`) || ''}
                                        </div>
                                        <div className="address-line">
                                            <i className="fas fa-road text-muted me-2"></i>
                                            {(order.sales_order_shipping_address || order.salesOrderShippingAddress).street}
                                            {((order.sales_order_shipping_address || order.salesOrderShippingAddress).block && `, ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).block}`) || ''}
                                        </div>
                                        <div className="address-line">
                                            <i className="fas fa-map-marked-alt text-muted me-2"></i>
                                            {(order.sales_order_shipping_address || order.salesOrderShippingAddress).area}, {(order.sales_order_shipping_address || order.salesOrderShippingAddress).region}
                                            {((order.sales_order_shipping_address || order.salesOrderShippingAddress).postcode && ` - ${(order.sales_order_shipping_address || order.salesOrderShippingAddress).postcode}`) || ''}
                                        </div>
                                        <div className="address-line">
                                            <i className="fas fa-phone text-muted me-2"></i>
                                            {(order.sales_order_shipping_address || order.salesOrderShippingAddress).phoneNumber}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-3">
                                        <i className="fas fa-map-marker-alt text-muted fa-2x mb-2"></i>
                                        <p className="text-muted mb-0">No shipping address available</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="card">
                            <div className="card-body">
                                <div className="d-grid gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-sm"
                                        disabled={submitting || totals.totalQty === 0}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1"></span>
                                                <span className="d-none d-md-inline">Creating Invoice...</span>
                                                <span className="d-md-none">Creating...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-1"></i>
                                                <span className="d-none d-md-inline">Create Invoice</span>
                                                <span className="d-md-none">Create</span>
                                            </>
                                        )}
                                    </button>
                                    
                                    <Link 
                                        to={`/sales-orders/${order.orderId}`}
                                        className="btn btn-outline-secondary btn-sm"
                                    >
                                        <i className="fas fa-arrow-left me-1"></i>
                                        <span className="d-none d-md-inline">Back to Order</span>
                                        <span className="d-md-none">Back</span>
                                    </Link>
                                </div>

                                {totals.totalQty === 0 && (
                                    <div className="alert alert-warning mt-3 mb-0">
                                        <small>Please select at least one item with quantity greater than 0 to create an invoice.</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default InvoiceCreate

// Add CSS styles for address formatting and mobile responsiveness
const styles = `
    /* Address formatting styles similar to CustomerList.js */
    .address-details {
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        padding: 1rem;
        transition: all 0.2s ease;
    }

    .address-details:hover {
        border-color: #3b82f6;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    }

    .address-line {
        font-size: 13px;
        color: #4b5563;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: flex-start;
        line-height: 1.4;
    }

    .address-line:last-child {
        margin-bottom: 0;
    }

    .address-line i {
        margin-top: 0.125rem;
        flex-shrink: 0;
    }

    .address-line strong {
        color: #374151;
        font-weight: 600;
    }

    /* Mobile responsive adjustments */
    @media (max-width: 768px) {
        .card-body {
            padding: 1rem;
        }

        .table-responsive {
            font-size: 0.875rem;
        }

        .table th,
        .table td {
            padding: 0.5rem 0.25rem;
        }

        .form-control-sm {
            font-size: 0.875rem;
            padding: 0.25rem 0.5rem;
        }

        .address-details {
            padding: 0.75rem;
        }

        .address-line {
            font-size: 12px;
            margin-bottom: 0.375rem;
        }

        .card-header {
            padding: 0.75rem 1rem;
        }

        .card-header h5 {
            font-size: 1rem;
        }

        .card-header small {
            font-size: 0.75rem;
        }
    }

    @media (max-width: 576px) {
        .card-body {
            padding: 0.75rem;
        }

        .table-responsive {
            font-size: 0.8rem;
        }

        .table th,
        .table td {
            padding: 0.375rem 0.125rem;
        }

        .form-control-sm {
            font-size: 0.8rem;
            padding: 0.25rem 0.375rem;
        }

        .address-details {
            padding: 0.5rem;
        }

        .address-line {
            font-size: 11px;
            margin-bottom: 0.25rem;
        }

        .card-header {
            padding: 0.5rem 0.75rem;
        }

        .card-header h5 {
            font-size: 0.9rem;
        }

        .card-header small {
            font-size: 0.7rem;
        }

        /* Improve table readability on mobile */
        .table-striped > tbody > tr:nth-of-type(odd) > td {
            background-color: rgba(0, 0, 0, 0.02);
        }

        /* Make input fields more touch-friendly */
        input[type="number"] {
            min-height: 2rem;
        }

        /* Improve textarea on mobile */
        textarea.form-control {
            min-height: 4rem;
            font-size: 0.875rem;
        }
    }

    /* Enhanced mobile table styles */
    @media (max-width: 768px) {
        .table-responsive {
            border: 0;
        }

        .table-responsive .table {
            margin-bottom: 0;
        }

        .table-responsive .table thead {
            display: none;
        }

        .table-responsive .table tbody tr {
            display: block;
            margin-bottom: 1rem;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            background-color: white;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .table-responsive .table tbody td {
            display: block;
            text-align: left;
            border: none;
            padding: 0.5rem;
            position: relative;
        }

        .table-responsive .table tbody td:before {
            content: attr(data-label) ": ";
            font-weight: 600;
            color: #6c757d;
            margin-right: 0.5rem;
        }

        .table-responsive .table tbody td:first-child {
            background-color: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
            font-weight: 600;
        }
    }

    /* Improve promo discount input on mobile */
    @media (max-width: 576px) {
        .d-flex.flex-column.flex-md-row {
            gap: 0.5rem;
        }

        .d-flex.flex-column.flex-md-row input {
            width: 100% !important;
            min-width: auto !important;
        }
    }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
} 